/**
 * Rax Skills List Component
 *
 * Shows all available skills with capability cards.
 * Filter by category, search, see what's enabled.
 *
 * Polished UX matching RBAC controls style.
 */

import { LitElement, html, css, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import {
  EXAMPLE_CAPABILITIES,
  type SkillCapability,
} from "../../../../src/skills/capability-schema.js";
import {
  raxApi,
  type Agent,
  type GoogleSkillIntegration,
  type RuntimeSecretHealth,
  type SecretInfo,
} from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";
import "./skill-card.js";

type SkillCategory =
  | "all"
  | "foundational"
  | "communication"
  | "productivity"
  | "data"
  | "automation"
  | "integration"
  | "other";
type SkillAuthStatus = "connected" | "disconnected" | "expired";

// Foundational skills - these unlock everything else (browser + messaging)
// Browser = agent can do anything on the web
// iMessage = user can reach agent anywhere, control plane for configuration
const FOUNDATIONAL_SKILL_IDS = ["kernel-browser", "browser", "web-browse", "imessage"];
type SkillWithState = SkillCapability & {
  enabled: boolean;
  authStatus?: SkillAuthStatus;
};
type ConnectionPresetId =
  | "github"
  | "openai"
  | "anthropic"
  | "openrouter"
  | "google"
  | "slack"
  | "notion"
  | "custom";

type ConnectionPreset = {
  id: ConnectionPresetId;
  type: string;
  defaultName: string;
};

const CONNECTION_PRESETS: ConnectionPreset[] = [
  { id: "github", type: "custom_api_key", defaultName: "GITHUB_PAT" },
  { id: "openai", type: "openai_api_key", defaultName: "OPENAI_API_KEY" },
  { id: "anthropic", type: "anthropic_api_key", defaultName: "ANTHROPIC_API_KEY" },
  { id: "openrouter", type: "openrouter_api_key", defaultName: "OPENROUTER_API_KEY" },
  { id: "google", type: "google_api_key", defaultName: "GEMINI_API_KEY" },
  { id: "slack", type: "custom_api_key", defaultName: "SLACK_BOT_TOKEN" },
  { id: "notion", type: "custom_api_key", defaultName: "NOTION_API_KEY" },
  { id: "custom", type: "custom_api_key", defaultName: "CUSTOM_SECRET" },
];

// Category config - icons removed from tabs to reduce visual clutter
// (skill cards already have icons, page header has main icon)
const CATEGORY_CONFIG: Record<SkillCategory, { label: string; description?: string }> = {
  all: { label: "All" },
  foundational: {
    label: "Foundational",
    description: "Core capabilities that unlock everything else",
  },
  communication: { label: "Communication" },
  productivity: { label: "Productivity" },
  data: { label: "Data" },
  automation: { label: "Automation" },
  integration: { label: "Integration" },
  other: { label: "Other" },
};

// Helper to check if a skill is foundational
function isFoundationalSkill(skillId: string): boolean {
  const id = skillId.toLowerCase();
  return FOUNDATIONAL_SKILL_IDS.some((fid) => id.includes(fid) || fid.includes(id));
}

@customElement("rax-skills-list")
export class RaxSkillsList extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .skills-page {
        padding: var(--space-6);
        max-width: 1400px;
        margin: 0 auto;
      }
      
      /* Page Header */
      .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: var(--space-6);
        gap: var(--space-4);
        flex-wrap: wrap;
      }
      
      .page-title-section {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .page-title {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        font-size: var(--font-2xl);
        font-weight: 700;
        color: var(--text-primary);
        margin: 0;
      }
      
      .page-title-icon {
        font-size: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        background: linear-gradient(135deg, var(--rax-yellow), var(--rax-gold));
        border-radius: var(--radius-lg);
      }
      
      .page-subtitle {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        max-width: 500px;
      }
      
      /* Agent Selector */
      .agent-selector {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        margin-top: var(--space-3);
        padding: var(--space-3) var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
      }
      
      .agent-selector-label {
        font-size: var(--font-sm);
        color: var(--text-secondary);
        white-space: nowrap;
      }
      
      .agent-select {
        flex: 1;
        padding: var(--space-2) var(--space-3);
        background: var(--bg-primary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        color: var(--text-primary);
        font-size: var(--font-sm);
        font-weight: 500;
        cursor: pointer;
        transition: all var(--transition-fast);
        min-width: 180px;
      }
      
      .agent-select:hover {
        border-color: var(--text-tertiary);
      }
      
      .agent-select:focus {
        outline: none;
        border-color: var(--rax-yellow);
        box-shadow: 0 0 0 3px rgba(255, 208, 0, 0.15);
      }
      
      .agent-select option {
        background: var(--bg-primary);
        color: var(--text-primary);
        padding: var(--space-2);
      }
      
      /* Search */
      .search-container {
        display: flex;
        align-items: center;
        gap: var(--space-3);
      }
      
      .search-box {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        padding: var(--space-3) var(--space-4);
        min-width: 280px;
        transition: all var(--transition-fast);
      }
      
      .search-box:focus-within {
        border-color: var(--rax-yellow);
        box-shadow: 0 0 0 3px rgba(255, 208, 0, 0.15);
      }
      
      .search-box input {
        flex: 1;
        border: none;
        background: transparent;
        font-size: var(--font-sm);
        outline: none;
        color: var(--text-primary);
      }
      
      .search-box input::placeholder {
        color: var(--text-tertiary);
      }
      
      .search-icon {
        color: var(--text-tertiary);
        font-size: 16px;
      }
      
      .refresh-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 44px;
        height: 44px;
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .refresh-btn:hover {
        border-color: var(--rax-yellow);
        color: var(--rax-yellow);
      }
      
      /* Stats Cards */
      .stats-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: var(--space-4);
        margin-bottom: var(--space-6);
      }
      
      .stat-card {
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        padding: var(--space-4) var(--space-5);
        display: flex;
        align-items: center;
        gap: var(--space-4);
        transition: all var(--transition-fast);
      }
      
      .stat-card:hover {
        border-color: var(--text-tertiary);
      }
      
      .stat-icon {
        width: 48px;
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(--radius-md);
        font-size: 22px;
      }
      
      .stat-icon.available {
        background: rgba(255, 208, 0, 0.15);
      }
      
      .stat-icon.enabled {
        background: rgba(16, 185, 129, 0.15);
      }
      
      .stat-icon.connected {
        background: rgba(59, 130, 246, 0.15);
      }
      
      .stat-icon.shown {
        background: rgba(139, 92, 246, 0.15);
      }
      
      .stat-content {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }
      
      .stat-value {
        font-size: var(--font-2xl);
        font-weight: 700;
        color: var(--text-primary);
        line-height: 1;
      }
      
      .stat-label {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      /* Runtime Health Banner */
      .health-banner {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-3) var(--space-4);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
        margin-bottom: var(--space-5);
      }
      
      .health-banner.ok {
        background: rgba(16, 185, 129, 0.1);
        border: 1px solid rgba(16, 185, 129, 0.3);
        color: #86efac;
      }
      
      .health-banner.warn {
        background: rgba(245, 158, 11, 0.1);
        border: 1px solid rgba(245, 158, 11, 0.3);
        color: #fcd34d;
      }
      
      .health-icon {
        font-size: 16px;
      }
      
      /* Category Tabs */
      .category-section {
        margin-bottom: var(--space-5);
      }
      
      .category-tabs {
        display: flex;
        gap: var(--space-2);
        flex-wrap: wrap;
        padding: var(--space-1);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
      }
      
      .category-tab {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-4);
        border: none;
        border-radius: var(--radius-md);
        background: transparent;
        font-size: var(--font-sm);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-weight: 500;
      }
      
      .category-tab:hover {
        background: var(--bg-elevated);
        color: var(--text-primary);
      }
      
      .category-tab.active {
        background: var(--rax-yellow);
        color: var(--rax-black);
        font-weight: 600;
      }
      
      .category-tab .tab-icon {
        font-size: 14px;
      }
      
      .category-tab .tab-count {
        font-size: var(--font-xs);
        padding: 2px 6px;
        border-radius: var(--radius-sm);
        background: rgba(0, 0, 0, 0.1);
      }
      
      .category-tab.active .tab-count {
        background: rgba(0, 0, 0, 0.2);
      }
      
      /* Foundational tab gets special treatment */
      .category-tab.foundational {
        border: 1px solid rgba(255, 208, 0, 0.3);
        background: rgba(255, 208, 0, 0.08);
      }
      
      .category-tab.foundational:hover {
        border-color: var(--rax-yellow);
        background: rgba(255, 208, 0, 0.15);
      }
      
      .category-tab.foundational.active {
        border-color: var(--rax-yellow);
      }
      
      /* Skills Grid */
      .skills-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
        gap: var(--space-4);
      }
      
      /* Empty State */
      .empty-state {
        text-align: center;
        padding: var(--space-10);
        background: var(--bg-elevated);
        border: 1px dashed var(--border-color);
        border-radius: var(--radius-lg);
      }
      
      .empty-icon {
        font-size: 56px;
        margin-bottom: var(--space-4);
        opacity: 0.6;
      }
      
      .empty-title {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
        margin-bottom: var(--space-2);
      }
      
      .empty-text {
        color: var(--text-secondary);
        font-size: var(--font-sm);
      }
      
      /* Loading State */
      .loading-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: var(--space-10);
        gap: var(--space-4);
      }
      
      .loading-spinner {
        width: 48px;
        height: 48px;
        border: 3px solid var(--border-color);
        border-top-color: var(--rax-yellow);
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
      
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
      
      .loading-text {
        color: var(--text-secondary);
        font-size: var(--font-sm);
      }
      
      /* Connect Modal Overlay */
      .modal-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 100;
        padding: var(--space-4);
        backdrop-filter: blur(4px);
      }
      
      .connect-modal {
        width: min(520px, 100%);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-xl);
        overflow: hidden;
        box-shadow: var(--shadow-xl);
        animation: modalIn 0.2s ease-out;
      }
      
      @keyframes modalIn {
        from {
          opacity: 0;
          transform: scale(0.95) translateY(10px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }
      
      .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-4) var(--space-5);
        border-bottom: 1px solid var(--border-color);
        background: var(--bg-elevated);
      }
      
      .modal-title {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        font-size: var(--font-lg);
        font-weight: 700;
        color: var(--text-primary);
      }
      
      .modal-title-icon {
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, var(--rax-yellow), var(--rax-gold));
        border-radius: var(--radius-md);
        font-size: 18px;
      }
      
      .modal-close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: none;
        background: var(--bg-primary);
        border-radius: var(--radius-md);
        color: var(--text-secondary);
        cursor: pointer;
        transition: all var(--transition-fast);
        font-size: 18px;
      }
      
      .modal-close:hover {
        background: rgba(239, 68, 68, 0.15);
        color: var(--error);
      }
      
      .modal-body {
        padding: var(--space-5);
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
      }
      
      .modal-help {
        color: var(--text-secondary);
        font-size: var(--font-sm);
        line-height: 1.5;
      }
      
      .form-field {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      
      .form-label {
        font-size: var(--font-xs);
        font-weight: 600;
        color: var(--text-tertiary);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .form-input {
        padding: var(--space-3) var(--space-4);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        background: var(--bg-elevated);
        color: var(--text-primary);
        font-size: var(--font-sm);
        transition: all var(--transition-fast);
      }
      
      .form-input:focus {
        outline: none;
        border-color: var(--rax-yellow);
        box-shadow: 0 0 0 3px rgba(255, 208, 0, 0.15);
      }
      
      .form-input::placeholder {
        color: var(--text-tertiary);
      }
      
      .form-error {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-3);
        background: rgba(239, 68, 68, 0.1);
        border: 1px solid rgba(239, 68, 68, 0.3);
        border-radius: var(--radius-md);
        color: var(--error);
        font-size: var(--font-sm);
      }
      
      .modal-footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-3);
        padding: var(--space-4) var(--space-5);
        border-top: 1px solid var(--border-color);
        background: var(--bg-elevated);
      }
      
      .modal-btn {
        padding: var(--space-3) var(--space-5);
        border-radius: var(--radius-md);
        font-size: var(--font-sm);
        font-weight: 600;
        cursor: pointer;
        transition: all var(--transition-fast);
      }
      
      .modal-btn.secondary {
        background: var(--bg-primary);
        border: 1px solid var(--border-color);
        color: var(--text-secondary);
      }
      
      .modal-btn.secondary:hover {
        border-color: var(--text-tertiary);
        color: var(--text-primary);
      }
      
      .modal-btn.primary {
        background: var(--rax-yellow);
        border: none;
        color: var(--rax-black);
      }
      
      .modal-btn.primary:hover {
        background: var(--rax-gold);
        box-shadow: var(--shadow-glow);
      }
      
      .modal-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      /* Responsive */
      @media (max-width: 900px) {
        .stats-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      
      @media (max-width: 640px) {
        .skills-page {
          padding: var(--space-4);
        }
      
        .page-header {
          flex-direction: column;
        }
      
        .search-container {
          width: 100%;
        }
      
        .search-box {
          flex: 1;
          min-width: 0;
        }
      
        .stats-grid {
          grid-template-columns: repeat(2, 1fr);
          gap: var(--space-3);
        }
      
        .stat-card {
          padding: var(--space-3);
        }
      
        .stat-icon {
          width: 40px;
          height: 40px;
          font-size: 18px;
        }
      
        .stat-value {
          font-size: var(--font-xl);
        }
      
        .skills-grid {
          grid-template-columns: 1fr;
        }
      
        .category-tabs {
          padding: var(--space-2);
          gap: var(--space-1);
        }
      
        .category-tab {
          padding: var(--space-2) var(--space-3);
          font-size: var(--font-xs);
        }
      }
    `,
  ];

  @state()
  private skills: SkillWithState[] = [];

  @state()
  private loading = true;

  @state()
  private errorMessage: string | null = null;

  @state()
  private statusMessage: string | null = null;

  @state()
  private secretInventory: SecretInfo[] = [];

  @state()
  private targetAgentId: string | null = null;

  @state()
  private targetAgentName: string | null = null;

  @state()
  private allAgents: Agent[] = [];

  @state()
  private runtimeHealth: RuntimeSecretHealth | null = null;

  @property({ type: String })
  agentId?: string;

  @state()
  private category: SkillCategory = "all";

  @state()
  private searchQuery = "";

  @state()
  private showConnectModal = false;

  @state()
  private connectSkillId: string | null = null;

  @state()
  private connectSecretName = "";

  @state()
  private connectSecretValue = "";

  @state()
  private connectSaving = false;

  @state()
  private connectError: string | null = null;

  override async connectedCallback() {
    super.connectedCallback();
    await this.loadSkills();
  }

  override async updated(changedProps: PropertyValues<this>) {
    if (changedProps.has("agentId")) {
      await this.loadSkills();
    }
  }

  private async loadSkills() {
    this.loading = true;
    this.errorMessage = null;
    this.statusMessage = null;
    this.runtimeHealth = null;
    try {
      const [agentRes, secretRes, runtimeHealthRes] = await Promise.all([
        raxApi.listAgents(),
        raxApi.listSecrets(),
        raxApi.getRuntimeSecretHealth(),
      ]);
      const agents = agentRes.success && agentRes.data ? agentRes.data : [];
      const secrets = secretRes.success && secretRes.data ? secretRes.data : [];
      this.runtimeHealth =
        runtimeHealthRes.success && runtimeHealthRes.data ? runtimeHealthRes.data : null;
      this.secretInventory = secrets;
      this.allAgents = agents;

      const targetAgent = this.pickTargetAgent(agents);
      const enabledSkillIds = new Set(targetAgent?.enabledSkills ?? []);
      this.targetAgentId = targetAgent?.id ?? null;
      this.targetAgentName = targetAgent?.name ?? null;

      this.skills = Object.values(EXAMPLE_CAPABILITIES).map((skill) => ({
        ...skill,
        enabled: enabledSkillIds.has(skill.id),
        authStatus: this.resolveAuthStatus(skill, secrets),
      }));

      if (!agentRes.success) {
        this.errorMessage = agentRes.error ?? "Failed to load agents.";
      } else if (!targetAgent) {
        this.errorMessage = "Create an agent to enable skills.";
      } else if (!secretRes.success) {
        this.errorMessage = secretRes.error ?? "Failed to load connection status.";
      }
    } catch (err) {
      console.error("[SkillsList] Failed to load skills:", err);
      this.errorMessage = err instanceof Error ? err.message : "Failed to load skills";
    } finally {
      this.loading = false;
    }
  }

  override render() {
    if (this.loading) {
      return this.renderLoading();
    }

    const filteredSkills = this.getFilteredSkills();
    const enabledCount = this.skills.filter((s) => s.enabled).length;
    const authRequiredCount = this.skills.filter((s) => s.requiresAuth).length;
    const connectedCount = this.skills.filter(
      (s) => s.requiresAuth && s.authStatus === "connected",
    ).length;

    return html`
      <div class="skills-page">
        ${this.renderHeader()}
        ${this.renderStats(enabledCount, connectedCount, authRequiredCount, filteredSkills.length)}
        ${this.renderHealthBanner()}
        ${this.renderCategoryTabs()}
        ${this.renderSkillsGrid(filteredSkills)}
      </div>
      ${this.showConnectModal ? this.renderConnectModal() : ""}
    `;
  }

  private renderLoading() {
    return html`
      <div class="skills-page">
        <div class="loading-state">
          <div class="loading-spinner"></div>
          <div class="loading-text">Loading skills...</div>
        </div>
      </div>
    `;
  }

  private renderHeader() {
    return html`
      <div class="page-header">
        <div class="page-title-section">
          <h1 class="page-title">
            <span class="page-title-icon">⚡</span>
            Skills & Capabilities
          </h1>
          <p class="page-subtitle">
            Enable and configure the abilities your agent can use.
            Each skill has specific permissions and data access rules.
          </p>
          ${
            this.allAgents.length > 0
              ? html`
            <div class="agent-selector">
              <label class="agent-selector-label">Managing skills for:</label>
              <select
                class="agent-select"
                .value=${this.targetAgentId || ""}
                @change=${this.onAgentChange}
              >
                ${this.allAgents.map(
                  (agent) => html`
                  <option value=${agent.id} ?selected=${agent.id === this.targetAgentId}>
                    ${agent.icon} ${agent.name}
                  </option>
                `,
                )}
              </select>
            </div>
          `
              : ""
          }
        </div>
        <div class="search-container">
          <div class="search-box">
            <span class="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search skills..."
              .value=${this.searchQuery}
              @input=${this.onSearch}
            />
          </div>
          <button class="refresh-btn" @click=${() => this.loadSkills()} title="Refresh">
            🔄
          </button>
        </div>
      </div>
      ${
        this.errorMessage
          ? html`
        <div class="health-banner warn">
          <span class="health-icon">⚠️</span>
          ${this.errorMessage}
        </div>
      `
          : ""
      }
      ${
        this.statusMessage
          ? html`
        <div class="health-banner ok">
          <span class="health-icon">✓</span>
          ${this.statusMessage}
        </div>
      `
          : ""
      }
    `;
  }

  private renderStats(enabled: number, connected: number, authRequired: number, _shown: number) {
    return html`
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon available">📦</div>
          <div class="stat-content">
            <div class="stat-value">${this.skills.length}</div>
            <div class="stat-label">Available</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon enabled">✓</div>
          <div class="stat-content">
            <div class="stat-value">${enabled}</div>
            <div class="stat-label">Enabled</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon connected">🔗</div>
          <div class="stat-content">
            <div class="stat-value">${connected}/${authRequired}</div>
            <div class="stat-label">Connected</div>
          </div>
        </div>
      </div>
    `;
  }

  private renderHealthBanner() {
    if (!this.runtimeHealth) return "";

    if (!this.runtimeHealth.enabled) {
      return html`
        <div class="health-banner warn">
          <span class="health-icon">⚠️</span>
          Runtime broker not configured. Skills can connect, but runtime sync stays unavailable until
          configured.
        </div>
      `;
    }

    if (!this.runtimeHealth.ok) {
      return html`
        <div class="health-banner warn">
          <span class="health-icon">⚠️</span>
          ${this.runtimeHealth.message}
        </div>
      `;
    }

    return html`
      <div class="health-banner ok">
        <span class="health-icon">✓</span>
        ${this.runtimeHealth.message}
      </div>
    `;
  }

  private renderCategoryTabs() {
    const getCategoryCount = (cat: SkillCategory) => {
      if (cat === "all") return this.skills.length;
      if (cat === "foundational")
        return this.skills.filter((s) => isFoundationalSkill(s.id)).length;
      return this.skills.filter((s) => s.category === cat).length;
    };

    return html`
      <div class="category-section">
        <div class="category-tabs">
          ${(Object.keys(CATEGORY_CONFIG) as SkillCategory[]).map((cat) => {
            const config = CATEGORY_CONFIG[cat];
            const count = getCategoryCount(cat);
            // Always show foundational tab even if count is 0 (to highlight importance)
            if (cat !== "all" && cat !== "foundational" && count === 0) return "";

            return html`
              <button
                class="category-tab ${this.category === cat ? "active" : ""} ${cat === "foundational" ? "foundational" : ""}"
                @click=${() => (this.category = cat)}
              >
                ${cat === "foundational" ? "⚡ " : ""}${config.label}
                <span class="tab-count">${count}</span>
              </button>
            `;
          })}
        </div>
      </div>
    `;
  }

  private renderSkillsGrid(skills: SkillWithState[]) {
    if (skills.length === 0) {
      return this.renderEmptyState();
    }

    return html`
      <div class="skills-grid">
        ${skills.map(
          (skill) => html`
          <rax-skill-card
            .skill=${skill}
            @skill-toggle=${this.onSkillToggle}
            @skill-connect=${this.onSkillConnect}
            @skill-disconnect=${this.onSkillDisconnect}
          ></rax-skill-card>
        `,
        )}
      </div>
    `;
  }

  private renderEmptyState() {
    if (this.searchQuery) {
      return html`
        <div class="empty-state">
          <div class="empty-icon">🔍</div>
          <div class="empty-title">No skills found</div>
          <div class="empty-text">No skills match "${this.searchQuery}". Try a different search term.</div>
        </div>
      `;
    }

    return html`
      <div class="empty-state">
        <div class="empty-icon">📭</div>
        <div class="empty-title">No skills in this category</div>
        <div class="empty-text">
          Try selecting a different category or check back later for new skills.
        </div>
      </div>
    `;
  }

  private renderConnectModal() {
    const skill = this.skills.find((entry) => entry.id === this.connectSkillId);

    return html`
      <div class="modal-overlay" @click=${this.closeConnectModal}>
        <div class="connect-modal" @click=${(e: Event) => e.stopPropagation()}>
          <div class="modal-header">
            <div class="modal-title">
              <span class="modal-title-icon">${skill?.icon || "🔗"}</span>
              Connect ${skill?.name || "Skill"}
            </div>
            <button class="modal-close" @click=${this.closeConnectModal}>×</button>
          </div>

          <div class="modal-body">
            <p class="modal-help">
              Store this provider key directly from Skills. The secret remains in your encrypted vault
              and is only accessed when the skill runs.
            </p>

            <div class="form-field">
              <label class="form-label">Secret Name</label>
              <input
                class="form-input"
                .value=${this.connectSecretName}
                @input=${(e: Event) => (this.connectSecretName = (e.target as HTMLInputElement).value)}
                placeholder="e.g., SLACK_BOT_TOKEN"
              />
            </div>

            <div class="form-field">
              <label class="form-label">Secret Value</label>
              <input
                class="form-input"
                type="password"
                .value=${this.connectSecretValue}
                @input=${(e: Event) => (this.connectSecretValue = (e.target as HTMLInputElement).value)}
                placeholder="Paste your API key or token"
              />
            </div>

            ${
              this.connectError
                ? html`
              <div class="form-error">
                <span>⚠️</span>
                ${this.connectError}
              </div>
            `
                : ""
            }
          </div>

          <div class="modal-footer">
            <button class="modal-btn secondary" @click=${this.closeConnectModal}>
              Cancel
            </button>
            <button
              class="modal-btn primary"
              @click=${this.saveConnectSecret}
              ?disabled=${this.connectSaving}
            >
              ${this.connectSaving ? "Connecting..." : "Connect"}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private getFilteredSkills(): SkillWithState[] {
    let filtered = this.skills;

    if (this.category === "foundational") {
      // Show foundational skills (browser + iMessage)
      filtered = filtered.filter((s) => isFoundationalSkill(s.id));
    } else if (this.category !== "all") {
      filtered = filtered.filter((s) => s.category === this.category);
    }

    if (this.searchQuery) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.name.toLowerCase().includes(query) ||
          s.description.toLowerCase().includes(query) ||
          s.scenarios.some((sc) => sc.toLowerCase().includes(query)),
      );
    }

    // Sort foundational skills to the top when showing all
    if (this.category === "all" && !this.searchQuery) {
      filtered = [...filtered].sort((a, b) => {
        const aFoundational = isFoundationalSkill(a.id);
        const bFoundational = isFoundationalSkill(b.id);
        if (aFoundational && !bFoundational) return -1;
        if (!aFoundational && bFoundational) return 1;
        return 0;
      });
    }

    return filtered;
  }

  private onSearch(e: InputEvent) {
    this.searchQuery = (e.target as HTMLInputElement).value;
  }

  private async onAgentChange(e: Event) {
    const select = e.target as HTMLSelectElement;
    const newAgentId = select.value;
    if (newAgentId && newAgentId !== this.targetAgentId) {
      this.targetAgentId = newAgentId;
      const agent = this.allAgents.find((a) => a.id === newAgentId);
      this.targetAgentName = agent?.name ?? null;

      // Reload skills with new agent's enabled skills
      const enabledSkillIds = new Set(agent?.enabledSkills ?? []);
      this.skills = this.skills.map((skill) => ({
        ...skill,
        enabled: enabledSkillIds.has(skill.id),
      }));
    }
  }

  private async onSkillToggle(e: CustomEvent<{ skillId: string; enabled: boolean }>) {
    const { skillId, enabled } = e.detail;
    const agentId = this.targetAgentId ?? this.agentId ?? null;
    if (!agentId) {
      this.errorMessage = "Create an agent before enabling skills.";
      return;
    }

    this.errorMessage = null;
    const previous = this.skills;
    this.skills = this.skills.map((skill) =>
      skill.id === skillId ? { ...skill, enabled } : skill,
    );

    const response = enabled
      ? await raxApi.enableSkill(agentId, skillId)
      : await raxApi.disableSkill(agentId, skillId);

    if (!response.success) {
      this.skills = previous;
      this.errorMessage =
        response.error ?? `Failed to ${enabled ? "enable" : "disable"} ${skillId}.`;
    }
  }

  private async onSkillConnect(e: CustomEvent<{ skillId: string }>) {
    const skill = this.skills.find((entry) => entry.id === e.detail.skillId);
    if (!skill) return;

    const googleIntegration = this.resolveGoogleSkillIntegration(skill);
    if (googleIntegration) {
      await this.connectGoogleSkillOAuth(skill, googleIntegration);
      return;
    }

    const presetId = this.resolveConnectionPreset(skill);
    const preset = presetId ? CONNECTION_PRESETS.find((entry) => entry.id === presetId) : null;
    if (!preset) {
      this.errorMessage = "No direct provider mapping was found. Use Connections for this skill.";
      return;
    }

    this.errorMessage = null;
    this.statusMessage = null;
    this.connectSkillId = skill.id;
    this.connectSecretName = preset.defaultName;
    this.connectSecretValue = "";
    this.connectError = null;
    this.showConnectModal = true;
  }

  private async onSkillDisconnect(e: CustomEvent<{ skillId: string }>) {
    const skill = this.skills.find((entry) => entry.id === e.detail.skillId);
    if (!skill) return;

    const googleIntegration = this.resolveGoogleSkillIntegration(skill);
    if (googleIntegration) {
      this.errorMessage = null;
      this.statusMessage = null;
      const revoke = await raxApi.revokeGoogleSkillOAuth(googleIntegration);
      if (!revoke.success) {
        this.errorMessage = revoke.error || `Failed to disconnect ${skill.name}.`;
        return;
      }
      await this.loadSkills();
      this.statusMessage = `Disconnected ${skill.name}.`;
      return;
    }

    const secretIds = this.resolveDisconnectSecretIds(skill);
    if (secretIds.length === 0) {
      this.errorMessage = "No matching connection secret found for this skill.";
      return;
    }

    this.errorMessage = null;
    this.statusMessage = null;
    for (const secretId of secretIds) {
      const result = await raxApi.deleteSecret(secretId);
      if (!result.success) {
        this.errorMessage = result.error || "Failed to disconnect provider secret.";
        return;
      }
    }
    await this.loadSkills();
    this.statusMessage = `Disconnected ${skill.name}.`;
  }

  private closeConnectModal() {
    this.showConnectModal = false;
    this.connectSkillId = null;
    this.connectSecretValue = "";
    this.connectError = null;
  }

  private async saveConnectSecret() {
    const skill = this.skills.find((entry) => entry.id === this.connectSkillId);
    if (!skill) {
      this.connectError = "Skill context missing. Reopen connect and retry.";
      return;
    }
    const presetId = this.resolveConnectionPreset(skill);
    const preset = presetId ? CONNECTION_PRESETS.find((entry) => entry.id === presetId) : null;
    if (!preset) {
      this.connectError = "Provider preset missing.";
      return;
    }
    const name = this.connectSecretName.trim();
    const value = this.connectSecretValue.trim();
    if (!name || !value) {
      this.connectError = "Secret name and value are required.";
      return;
    }

    this.connectSaving = true;
    this.connectError = null;
    try {
      const result = await raxApi.storeSecret({
        type: preset.type,
        name,
        value,
        metadata: {
          source: "skills_tab",
          preset: preset.id,
          skillId: skill.id,
        },
      });
      if (!result.success) {
        this.connectError = result.error || "Could not store provider secret.";
        return;
      }
      this.closeConnectModal();
      await this.loadSkills();
      this.statusMessage = `Connected ${skill.name}.`;
    } catch (error) {
      this.connectError = String(error);
    } finally {
      this.connectSaving = false;
    }
  }

  private async connectGoogleSkillOAuth(
    skill: SkillCapability,
    integration: GoogleSkillIntegration,
  ): Promise<void> {
    this.errorMessage = null;
    this.statusMessage = null;
    const start = await raxApi.startGoogleSkillOAuth(integration);
    if (!start.success || !start.data?.authorizationUrl) {
      this.errorMessage = start.error || `Failed to start ${skill.name} OAuth flow.`;
      return;
    }

    const popup = window.open(
      start.data.authorizationUrl,
      `rax-google-oauth-${integration}`,
      "popup=yes,width=520,height=700,resizable=yes,scrollbars=yes",
    );
    if (!popup) {
      this.errorMessage = "OAuth popup was blocked. Allow popups and retry.";
      return;
    }

    this.statusMessage = `Waiting for ${skill.name} authorization...`;
    const result = await this.waitForGoogleOAuthResult(popup);
    if (!result.success) {
      this.errorMessage = result.message || `Failed to connect ${skill.name}.`;
      return;
    }

    await this.loadSkills();
    this.statusMessage = `Connected ${skill.name}.`;
  }

  private waitForGoogleOAuthResult(
    popup: Window,
  ): Promise<{ success: boolean; integration?: GoogleSkillIntegration; message?: string }> {
    const timeoutMs = 120_000;
    const startedAt = Date.now();
    return new Promise((resolve) => {
      let settled = false;
      let pollHandle: number | null = null;

      const finish = (result: {
        success: boolean;
        integration?: GoogleSkillIntegration;
        message?: string;
      }) => {
        if (settled) return;
        settled = true;
        window.removeEventListener("message", onMessage);
        if (pollHandle) window.clearInterval(pollHandle);
        try {
          if (!popup.closed) popup.close();
        } catch {
          /* noop */
        }
        resolve(result);
      };

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        const payload = event.data as {
          source?: string;
          success?: boolean;
          integration?: string | null;
          message?: string;
        };
        if (!payload || payload.source !== "rax-google-skill-oauth") return;
        const integration = payload.integration;
        finish({
          success: payload.success === true,
          integration:
            typeof integration === "string" &&
            (integration === "gmail" || integration === "calendar")
              ? integration
              : undefined,
          message: payload.message || undefined,
        });
      };

      window.addEventListener("message", onMessage);
      pollHandle = window.setInterval(() => {
        if (settled) return;
        if (popup.closed) {
          finish({ success: false, message: "OAuth popup was closed before completion." });
          return;
        }
        if (Date.now() - startedAt > timeoutMs) {
          finish({ success: false, message: "OAuth flow timed out. Retry connection." });
        }
      }, 350);
    });
  }

  private resolveDisconnectSecretIds(skill: SkillCapability): string[] {
    const provider = this.resolveConnectionPreset(skill);
    if (!provider) return [];
    const providerNameHints: Record<ConnectionPresetId, string[]> = {
      github: ["github", "gh_token", "gh_pat", "github_pat", "github_token"],
      openai: ["openai", "gpt"],
      anthropic: ["anthropic", "claude"],
      openrouter: ["openrouter"],
      google: ["google", "gemini", "gmail", "calendar"],
      slack: ["slack", "xoxb", "xoxp"],
      notion: ["notion"],
      custom: [skill.id.toLowerCase()],
    };
    const hints = providerNameHints[provider] || [];
    return this.secretInventory
      .filter((secret) => {
        const normalized = secret.name.toLowerCase();
        return hints.some((hint) => normalized.includes(hint));
      })
      .map((secret) => secret.id);
  }

  private resolveConnectionPreset(skill: SkillCapability): ConnectionPresetId | null {
    const skillId = skill.id.toLowerCase();
    const provider = (skill.authProvider || "").toLowerCase();
    if (skillId.includes("gmail") || skillId.includes("calendar")) return null;
    if (provider === "google") return "google";
    if (provider === "slack") return "slack";
    if (provider === "notion") return "notion";
    if (provider === "github") return "github";
    if (provider === "custom") return "custom";
    if (skillId.includes("openai")) return "openai";
    if (skillId.includes("anthropic")) return "anthropic";
    if (skillId.includes("openrouter")) return "openrouter";
    if (skillId.includes("slack")) return "slack";
    if (skillId.includes("notion")) return "notion";
    return null;
  }

  private resolveGoogleSkillIntegration(skill: SkillCapability): GoogleSkillIntegration | null {
    const id = skill.id.toLowerCase();
    if (id === "gmail" || id.includes("gmail")) return "gmail";
    if (id === "calendar" || id.includes("calendar")) return "calendar";
    return null;
  }

  private pickTargetAgent(agents: Agent[]): Agent | null {
    if (agents.length === 0) return null;
    if (this.agentId) {
      const requested = agents.find((agent) => agent.id === this.agentId);
      if (requested) return requested;
    }
    return agents.find((agent) => agent.status === "active") ?? agents[0] ?? null;
  }

  private resolveAuthStatus(
    skill: SkillCapability,
    secrets: SecretInfo[],
  ): SkillAuthStatus | undefined {
    if (!skill.requiresAuth) return undefined;
    const hasCredential = this.hasMatchingCredential(skill.id, skill.authProvider, secrets);
    if (!hasCredential) return "disconnected";
    if (this.runtimeHealth?.enabled && !this.runtimeHealth.ok) return "expired";
    return "connected";
  }

  private hasMatchingCredential(
    skillId: string,
    authProvider: SkillCapability["authProvider"],
    secrets: SecretInfo[],
  ): boolean {
    if (skillId === "gmail" || skillId === "calendar") {
      const integration = skillId as GoogleSkillIntegration;
      return secrets.some((secret) => this.inferGoogleSkillIntegration(secret) === integration);
    }

    const secretNames = secrets.map((secret) => secret.name.toLowerCase());
    const skillTokens = [skillId.toLowerCase()];
    if (authProvider) skillTokens.push(authProvider.toLowerCase());

    return secretNames.some((name) => {
      if (skillTokens.some((token) => token && name.includes(token))) return true;
      if (skillId === "gmail") return name.includes("gmail") || name.includes("google");
      if (skillId === "calendar") return name.includes("calendar") || name.includes("google");
      return false;
    });
  }

  private inferGoogleSkillIntegration(secret: SecretInfo): GoogleSkillIntegration | null {
    if ((secret.type || "").toLowerCase() !== "oauth_refresh_token") return null;

    const metadata = secret.metadata || {};
    const metadataIntegration = String(
      metadata.integration || metadata.skillId || metadata.skill || "",
    )
      .trim()
      .toLowerCase();
    if (metadataIntegration === "gmail" || metadataIntegration === "calendar") {
      return metadataIntegration;
    }

    const normalizedName = secret.name.toLowerCase();
    if (normalizedName.includes("gmail")) return "gmail";
    if (normalizedName.includes("calendar")) return "calendar";
    return null;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-skills-list": RaxSkillsList;
  }
}
